import { useEffect, useRef, useState } from 'react';

import { Badge, CopyButton } from '../common';
import { useRuntimeSettings } from '../../context/RuntimeSettingsContext';
import { formatMs, formatTime } from '../../utils/format';
import { ImageThumb } from './ImageThumb';
import { TargetStamp } from './ImageTarget';
import './chat.css';

export function MessageList({
  messages,
  pending,
  streaming,
  streamText,
  versionId,
  onFollowUp,
  onOpenAttributes,
  images = [],
}) {
  const endRef = useRef(null);
  const containerRef = useRef(null);
  const [pinned, setPinned] = useState(true);
  // Settings → General, "Suggested follow-up questions" (per organization).
  const followUps = useRuntimeSettings().effective.follow_up_suggestions || [];

  // Auto-scroll only while the user is already at the bottom: yanking the view
  // back down while they are reading an earlier answer is worse than not scrolling.
  useEffect(() => {
    if (pinned) endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, streamText, pending, pinned]);

  const onScroll = () => {
    const node = containerRef.current;
    if (!node) return;
    setPinned(node.scrollHeight - node.scrollTop - node.clientHeight < 90);
  };

  const lastAssistant = [...messages].reverse().find((message) => message.role === 'assistant');

  return (
    <div className="thread" ref={containerRef} onScroll={onScroll}>
      {messages.map((message) =>
        message.role === 'user' ? (
          <UserMessage key={message.id} message={message} versionId={versionId} images={images} />
        ) : (
          <AssistantMessage
            key={message.id}
            message={message}
            onOpenAttributes={onOpenAttributes}
            images={images}
          />
        ),
      )}

      {pending && <UserMessage message={pending} versionId={versionId} images={images} />}

      {streaming && (
        <div className="msg">
          <div className="msg__avatar msg__avatar--assistant" aria-hidden="true">
            Q
          </div>
          <div className="msg__body">
            <div className="msg__who">{versionId || 'model'} · generating…</div>
            {streamText ? (
              <div
                className={
                  looksTabular(streamText) ? 'msg__answer msg__answer--mono' : 'msg__answer'
                }
              >
                {streamText}
                <span className="cursor" aria-hidden="true" />
              </div>
            ) : (
              <div className="msg__answer subtle">
                Waiting for the first token
                <span className="cursor" aria-hidden="true" />
              </div>
            )}
          </div>
        </div>
      )}

      {!streaming && lastAssistant && !lastAssistant.error && followUps.length > 0 && (
        <div className="followups" style={{ marginLeft: 38 }}>
          {followUps.map((question) => (
            <button
              key={question}
              type="button"
              className="followup"
              onClick={() => onFollowUp(question)}
            >
              {question}
            </button>
          ))}
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
}

function UserMessage({ message, versionId, images = [] }) {
  return (
    <div className="msg">
      <div className="msg__avatar msg__avatar--user" aria-hidden="true">
        You
      </div>
      <div className="msg__body">
        <div className="msg__who">
          You {message.created_at && `· ${formatTime(message.created_at)}`}
          {message.pending && <span className="subtle">· sending…</span>}
          {/* Which picture this question was about. A transcript of nine follow-ups
              across three images is otherwise unreadable a week later, even though
              every row carries the answer. */}
          <TargetStamp images={images} imageId={message.target_image_id} />
        </div>
        {message.images?.length > 0 && (
          <div className="msg__images">
            {message.images.map((image) =>
              image.previewUrl ? (
                <span key={image.id || image.previewUrl} className="thumb">
                  <img src={image.previewUrl} alt={image.filename || ''} />
                  <span className="thumb__meta">
                    <span>{image.filename}</span>
                  </span>
                </span>
              ) : (
                <ImageThumb key={image.id} image={image} versionId={versionId} />
              ),
            )}
          </div>
        )}
        <div className="msg__text">{message.content}</div>
      </div>
    </div>
  );
}

function AssistantMessage({ message, onOpenAttributes, images = [] }) {
  const attributes = message.attributes;
  const counts = attributes?.counts;
  const isTabular = message.mode === 'extraction';

  return (
    <div className="msg">
      <div className="msg__avatar msg__avatar--assistant" aria-hidden="true">
        Q
      </div>
      <div className="msg__body">
        <div className="msg__who">
          {message.version_id || 'model'}
          {message.latency_ms ? ` · ${formatMs(message.latency_ms)}` : ''}
          {message.output_tokens ? ` · ${message.output_tokens} tokens` : ''}
          {message.truncated && <Badge tone="warn">truncated</Badge>}
          {message.mode === 'extraction' && <Badge tone="accent">extraction</Badge>}
          <TargetStamp images={images} imageId={message.target_image_id} />
        </div>

        {message.error ? (
          <div className="msg__error">{message.error}</div>
        ) : (
          <div className={isTabular ? 'msg__answer msg__answer--mono' : 'msg__answer'}>
            {message.raw_response}
          </div>
        )}

        {message.thinking && (
          <details className="msg__card">
            <summary className="msg__card-head" style={{ cursor: 'pointer' }}>
              Reasoning trace
            </summary>
            <div className="small muted" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
              {message.thinking}
            </div>
          </details>
        )}

        {attributes && (
          <div className="msg__card">
            <div className="msg__card-head">
              <span aria-hidden="true">▤</span> Structured output
              {counts && (
                <>
                  <Badge tone="ok">{counts.ok} valid</Badge>
                  {counts.fuzzy > 0 && <Badge tone="fuzzy">{counts.fuzzy} fuzzy</Badge>}
                  {counts.unmatched > 0 && <Badge tone="warn">{counts.unmatched} unmatched</Badge>}
                  {counts.missing > 0 && <Badge tone="miss">{counts.missing} missing</Badge>}
                </>
              )}
              <span style={{ marginLeft: 'auto' }} className="row">
                <CopyButton value={attributes.predictions} label="Copy JSON" />
                <button type="button" className="chip chip--button" onClick={onOpenAttributes}>
                  Open inspector →
                </button>
              </span>
            </div>
            {!attributes.has_structured_output && (
              <div className="small" style={{ color: 'var(--state-warn)', marginTop: 8 }}>
                Nothing parsed from this response. Check that the version’s attribute set and
                response scheme match how it was trained.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Numbered answer lines read better in a monospace block than as prose. */
function looksTabular(text) {
  const lines = text.trim().split('\n').slice(0, 4);
  return lines.length > 1 && lines.filter((line) => /^\s*\d{1,3}\s*:/.test(line)).length >= 2;
}
